import {isNanpTollFreeDestination,TOLL_FREE_BLOCK_MESSAGE} from '@bcio-voice/contracts';
// Typed destinations for POST /calls. The client only ever sends {kind:'internal',user_id}
// or {kind:'pstn',e164}; it never builds a SIP URI or dialstring.
import type { CreateCall, Destination } from '@bcio-voice/contracts';
import { parsePhoneInput, phoneParseMessage } from '../lib/format.ts';

export type DestinationDraft =
  | { kind: 'internal'; userId: string | null }
  | { kind: 'pstn'; input: string; fromNumberId: string | null };

export type DestinationCheck =
  | { ok: true; destination: Destination; fromNumberId: string | null }
  | { ok: false; field: 'user' | 'number' | 'from'; message: string; emergency?: boolean; tollFree?: boolean };

/** US emergency and test numbers (UX spec 6.2.11). These always get the dedicated state. */
const EMERGENCY = new Set(['911', '933', '112', '999']);

export function isEmergencyInput(input: string): boolean {
  const digits = input.replace(/[^0-9]/g, '');
  return EMERGENCY.has(digits) || EMERGENCY.has(digits.replace(/^1/, ''));
}

export function checkDestination(draft: DestinationDraft, selfUserId: string | null): DestinationCheck {
  if (draft.kind === 'internal') {
    if (!draft.userId) return { ok: false, field: 'user', message: 'Choose a teammate to call.' };
    if (draft.userId === selfUserId) return { ok: false, field: 'user', message: "You can't call yourself. Choose another teammate." };
    return { ok: true, destination: { kind: 'internal', user_id: draft.userId }, fromNumberId: null };
  }
  if (isEmergencyInput(draft.input)) {
    return {
      ok: false,
      field: 'number',
      emergency: true,
      message: 'Emergency calling isn’t available from this app. Use a mobile or desk phone now.',
    };
  }
  if (isNanpTollFreeDestination(draft.input)) return { ok: false, field: 'number', tollFree: true, message: TOLL_FREE_BLOCK_MESSAGE };
  const parsed = parsePhoneInput(draft.input);
  if (!parsed.ok) return { ok: false, field: 'number', message: phoneParseMessage(parsed) };
  if (isNanpTollFreeDestination(parsed.e164)) return { ok: false, field: 'number', tollFree: true, message: TOLL_FREE_BLOCK_MESSAGE };
  if (!draft.fromNumberId) return { ok: false, field: 'from', message: 'Choose a number to call from.' };
  return { ok: true, destination: { kind: 'pstn', e164: parsed.e164 }, fromNumberId: draft.fromNumberId };
}

export function buildCreateCall(check: Extract<DestinationCheck, { ok: true }>, deviceId: string, contactId: string | null): CreateCall {
  return {
    destination: check.destination,
    device_id: deviceId,
    ...(check.fromNumberId ? { from_number_id: check.fromNumberId } : {}),
    ...(contactId ? { contact_id: contactId } : {}),
  };
}

/** Dialpad keys accepted in the PSTN field and as DTMF. */
export const DIAL_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;
export const DIAL_LETTERS: Record<string, string> = {
  '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL', '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ', '0': '+',
};

export function validDtmf(digits: string): boolean {
  return /^[0-9*#A-D]{1,16}$/.test(digits);
}
